import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  SwapHoriz as TransferIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import transferService from '../../../../services/transferService';
import venueLocationService from '../../../../services/venueLocationService';
import type { Transfer } from '../../../../types/venue';

const formatDate = (d: string | null | undefined): string => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const TransferList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId, venueName } = useVenue();
  const base = `/admin/venues/${venueId}/transfers`;

  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locationNames, setLocationNames] = useState<Map<string, string>>(new Map());
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadTransfers = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await transferService.getTransfers(venueId, page + 1, pageSize);
      setTransfers(result.data);
      setTotal(result.pagination.totalItems);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load transfers');
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize]);

  useEffect(() => { loadTransfers(); }, [loadTransfers]);

  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(locs => {
        const map = new Map<string, string>();
        locs.forEach(l => map.set(l._id, l.name));
        setLocationNames(map);
      })
      .catch(() => {});
  }, [venueId]);

  const handleDelete = async (transferId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this draft transfer? This cannot be undone.')) return;
    setDeleting(transferId);
    try {
      await transferService.deleteTransfer(venueId, transferId);
      loadTransfers();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to delete transfer');
    } finally {
      setDeleting(null);
    }
  };

  const locationLabel = (id: string | null): string => {
    if (!id) return `${venueName} (General)`;
    return locationNames.get(id) ?? id;
  };

  const scopeLabel = (t: Transfer): string =>
    `${locationLabel(t.fromLocationId)} → ${locationLabel(t.toLocationId)}`;

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TransferIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>Transfers</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`${base}/create`)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            New Transfer
          </Button>
        </Box>

        {error && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Box>
        )}

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['From → To', 'Transfer Date', 'Transferred By', 'Posted', 'Status', ''].map(h => (
                      <TableCell
                        key={h}
                        sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.75rem', textTransform: 'uppercase' }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transfers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ py: 8, textAlign: 'center', color: theme.palette.text.secondary }}>
                          <TransferIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                          <Typography variant="body2">No transfers yet. Create one to move stock between locations.</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transfers.map(t => (
                      <TableRow
                        key={t._id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`${base}/${t._id}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{scopeLabel(t)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(t.transferredAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{t.transferredBy || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{formatDate(t.postedAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t.status === 'posted' ? 'Posted' : 'Draft'}
                            size="small"
                            color={t.status === 'posted' ? 'success' : 'default'}
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {t.status === 'draft' && (
                            <Tooltip title="Delete draft">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={e => handleDelete(t._id, e)}
                                  disabled={deleting === t._id}
                                  sx={{ color: theme.palette.text.disabled }}
                                >
                                  {deleting === t._id
                                    ? <CircularProgress size={16} />
                                    : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={pageSize}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default TransferList;
